import { useTranslation } from "react-i18next";

import type {
  PlanningConfig,
  useUpdatePlanningConfig,
} from "@/api/planningConfig";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { formatNumber } from "@/utils/formatters";

interface CleansingSectionProps {
  config: PlanningConfig;
  updateConfig: ReturnType<typeof useUpdatePlanningConfig>;
  excludedCount: number;
  onReview: () => void;
  disabled?: boolean;
}

export function CleansingSection({
  config,
  updateConfig,
  excludedCount,
  onReview,
  disabled = false,
}: CleansingSectionProps) {
  const { t } = useTranslation();
  const enabled = config.cleansingMethod === "mean_monthly_multiple";
  const factor = config.cleansingFactor ?? 0.5;

  function toggle(checked: boolean) {
    updateConfig.mutate({
      item: config.item,
      location: config.location,
      cleansingMethod: checked ? "mean_monthly_multiple" : "disabled",
    });
  }

  return (
    <div>
      <div className="mb-2">
        <span className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
          {t("items.detail.cleansing.sectionTitle")}
        </span>
      </div>

      <div className="flex items-center justify-between py-1">
        <Label
          htmlFor="cleansing-enable"
          className="text-muted-foreground text-xs"
        >
          {t("items.detail.cleansing.enableLabel")}
        </Label>
        <Switch
          id="cleansing-enable"
          checked={enabled}
          onCheckedChange={toggle}
          disabled={disabled || updateConfig.isPending}
        />
      </div>

      {enabled && (
        <div className="mt-2 flex flex-col gap-2">
          <div className="flex items-center gap-3">
            <Label
              htmlFor="cleansing-factor"
              className="text-muted-foreground text-xs"
            >
              {t("items.detail.cleansing.sensitivityLabel")}
            </Label>
            <Slider
              id="cleansing-factor"
              key={factor}
              defaultValue={[factor]}
              min={0.1}
              max={2.0}
              step={0.1}
              onValueCommit={([v]) =>
                updateConfig.mutate({
                  item: config.item,
                  location: config.location,
                  cleansingFactor: v,
                })
              }
              disabled={disabled || updateConfig.isPending}
              className="flex-1"
            />
            <span className="text-muted-foreground min-w-[90px] text-right text-xs tabular-nums">
              {formatNumber(factor, {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1,
              })}
              × {t("items.detail.cleansing.factorUnit")}
            </span>
          </div>
          <div className="text-muted-foreground flex items-center justify-between text-xs">
            <span>
              {t("items.detail.cleansing.eventsExcluded", {
                count: excludedCount,
              })}
            </span>
            <Button variant="ghost" size="sm" onClick={onReview}>
              {t("items.detail.cleansing.reviewButton")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
